import React from 'react';
import { useMyEvents } from 'clients/internal/homeService';
import { IMorressierEvent } from 'typings/event';
import UserEventsEmptyState from './EmptyUserEventsState';

import UserEvent from './UserEvent';
import { MainTitle, Divider } from './style';

import { SkeletonLoader } from 'containers/Search/Submissions';
import StartEvent, { EventModuleMapping } from './StartEvent';

interface Props {
  purchasedEventModules: string[];
  incompleteEvents: Record<string, string>;
}

const titleText = {
  HOSTING: 'MANAGE EVENTS',
  REVIEWING: 'PEER-REVIEW TASKS',
  ATTENDING: 'UPCOMING ATTENDANCE',
  '': 'MY EVENTS'
} as const;

type EventType = keyof typeof titleText;

const UserEvents = ({ purchasedEventModules, incompleteEvents }: Props) => {
  const { data, isLoading } = useMyEvents();
  const hostingEvents = data?.hostingEvents;
  const attendingEvents = data?.attendingEvents;
  const reviewingEvents = data?.reviewingEvents;

  const getIncompleteEvents = (events: IMorressierEvent[] = []) =>
    events.filter(({ id }) => Object.keys(incompleteEvents).includes(id));

  // Get the top 3 events that are not older than the last 60 days and sorted by start date
  const getEvents = (events: IMorressierEvent[] = []) => {
    const now = new Date();
    const last60DayDate = new Date();
    last60DayDate.setDate(now.getDate() - 60);
    const sortedEvents = events
      .filter(
        event =>
          !Object.keys(incompleteEvents).includes(event.id) &&
          new Date(event.end_date) > last60DayDate
      )
      .sort((event1, event2) => {
        const event1StartDate = new Date(event1.start_date);
        const event2StartDate = new Date(event2.start_date);
        return event1StartDate > event2StartDate
          ? 1
          : event1StartDate < event2StartDate
          ? -1
          : 0;
      })
      .slice(0, 3);
    return sortedEvents;
  };
  const pastTwoMonthsHostingEvents = getEvents(hostingEvents);
  const pastTwoMonthsAttendingEvents = getEvents(attendingEvents);
  const pastTwoMonthsReviewingEvents = getEvents(reviewingEvents);

  const renderEvents = (events: IMorressierEvent[], type: EventType = '') => {
    const renderEventList = () =>
      events?.map((event: IMorressierEvent) => (
        <UserEvent key={event.id} event={event} type={type} />
      ));

    return (
      <div>
        <MainTitle>{titleText[type]}</MainTitle>
        {type === 'HOSTING' && (
          <>
            {purchasedEventModules.map(module => (
              <StartEvent selectedModule={module} />
            ))}
            {uncompletedEvents.map(event => {
              const moduleName =
                EventModuleMapping[incompleteEvents[event.id]] || '';

              return (
                <UserEvent
                  key={event.id}
                  event={event}
                  type={type}
                  moduleName={moduleName}
                />
              );
            })}
          </>
        )}
        {renderEventList()}
        <Divider />
      </div>
    );
  };

  if (isLoading) {
    return (
      <>
        <SkeletonLoader />
        <SkeletonLoader />
      </>
    );
  }
  const uncompletedEvents = getIncompleteEvents(hostingEvents);

  const isHostingEvent =
    uncompletedEvents.length > 0 ||
    purchasedEventModules.length > 0 ||
    pastTwoMonthsHostingEvents?.length > 0;
  const isAttendingEvent = pastTwoMonthsAttendingEvents?.length > 0;
  const isReviewingEvent = pastTwoMonthsReviewingEvents?.length > 0;

  // Handle empty state
  if (!isLoading && !isAttendingEvent && !isReviewingEvent && !isHostingEvent) {
    return (
      <div>
        <MainTitle>MY EVENTS</MainTitle>
        <UserEventsEmptyState />
        <Divider />
      </div>
    );
  }

  return (
    <>
      {isHostingEvent && renderEvents(pastTwoMonthsHostingEvents, 'HOSTING')}
      {isReviewingEvent &&
        renderEvents(pastTwoMonthsReviewingEvents, 'REVIEWING')}
      {isAttendingEvent &&
        renderEvents(pastTwoMonthsAttendingEvents, 'ATTENDING')}
    </>
  );
};

export default UserEvents;
